import { useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { badgesActions } from 'pages/integrations/containers/BadgesContainer/actions';
import { badgesService } from 'services/integrations';
import { BADGE_EVENT_TYPE } from 'pages/integrations/containers/BadgesContainer/constants';
import { messageNotification } from 'helpers';

const BadgeGroupConfigModal = ({ group, provider = 'accredible', isOpen, onClose, dispatch }) => {
  const [courseKey, setCourseKey] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');

  // Initialize state from group
  useEffect(() => {
    if (group) {
      setIsActive(group.is_active || false);
      const requirement = group.requirements?.[0];
      const courseRule = requirement?.rules?.find((r) => r.data_path === 'course.course_key');
      setCourseKey(courseRule?.value || '');
      setError('');
    }
  }, [group]);

  const validateCourse = async () => {
    const key = courseKey.trim();
    if (!key) {
      setError('Course key is required.');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      await badgesService.validateCourseExists(key);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Course not found.' : 'Failed to validate course.');
    } finally {
      setIsValidating(false);
    }
  };

  const canSave = courseKey.trim() && !error && !isValidating && !isSaving;

  const handleSave = async () => {
    if (!group || !canSave) return;

    setIsSaving(true);

    try {
      const requirement = group.requirements?.[0];

      // Build the rules array with is_passing and course key
      const rules = [
        { data_path: 'is_passing', operator: 'eq', value: 'True' },
        { data_path: 'course.course_key', operator: 'eq', value: courseKey.trim() }
      ];

      if (requirement) {
        await dispatch(badgesActions.updateBadgeRequirement(requirement.id, { rules }));
      } else {
        await dispatch(
          badgesActions.createBadgeRequirement({
            template: group.id,
            event_type: BADGE_EVENT_TYPE,
            description: 'Auto-configured for course completion badge',
            rules
          })
        );
      }

      await dispatch(badgesActions.updateBadgeGroup(group.id, provider, { is_active: isActive }));

      messageNotification('success', 'Success', 'Badge configuration saved.');
      dispatch(badgesActions.fetchBadgeGroups(provider));
      onClose();
    } catch (err) {
      messageNotification('danger', 'Error', err.response?.data?.detail || 'Failed to save badge configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!group) return null;

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Configure Badge: {group.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="align-items-center mb-3">
          <Col xs={3}>
            <Form.Label className="mb-0">Course Key</Form.Label>
          </Col>
          <Col>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={courseKey}
                onChange={(e) => {
                  setCourseKey(e.target.value);
                  setError('');
                }}
                onBlur={validateCourse}
                placeholder="e.g., course-v1:edX+DemoX+Demo_Course"
                disabled={isSaving}
                isInvalid={!!error}
              />
              {isValidating && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                  <Spinner animation="border" size="sm" variant="secondary" />
                </div>
              )}
              <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            </div>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col xs={3}>
            <Form.Label className="mb-0">Enabled</Form.Label>
          </Col>
          <Col xs="auto">
            <Form.Check
              type="switch"
              id="badge-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isSaving}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleSave} disabled={!canSave}>
          {isSaving && <span className="spinner-border spinner-border-sm me-2" />}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default connect()(BadgeGroupConfigModal);
